import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { label: "Stay", target: "/stay", testid: "nav-link-stay" },
  { label: "The Company", target: "#manifesto", testid: "nav-link-about" },
  { label: "HostPoint Suite", target: "#flagships", testid: "nav-link-flagships" },
  { label: "All Systems", target: "#products", testid: "nav-link-products" },
  { label: "Contact", target: "#contact", testid: "nav-link-contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (location.pathname !== "/" || !location.hash) return undefined;
    const timeout = window.setTimeout(() => scrollToId(location.hash), 0);
    return () => window.clearTimeout(timeout);
  }, [location.pathname, location.hash]);

  const go = (target) => {
    setOpen(false);
    if (target.startsWith("/")) {
      navigate(target);
      return;
    }
    if (location.pathname !== "/") {
      navigate(`/${target}`);
      return;
    }
    scrollToId(target);
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-[#263562]/10 shadow-[0_4px_30px_rgba(38,53,98,0.06)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[70px] flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => {
            if (location.pathname === "/") {
              window.scrollTo({ top: 0, behavior: "smooth" });
            } else {
              navigate("/");
            }
          }}
          className="flex items-center group"
          aria-label="Hostech Solutions — back to top"
        >
          <img
            src="/assets/hostech-logo-horizontal.png"
            alt="Hostech Solutions — innovating hospitality"
            className="h-11 w-auto object-contain group-hover:scale-[1.03] transition-transform duration-300"
          />
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.testid}
              data-testid={l.testid}
              onClick={() => go(l.target)}
              className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-[#5A6B85] hover:text-[#263562] transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="cta-schedule-demo-nav"
            onClick={() => go("#contact")}
            className="group flex items-center gap-2 bg-[#263562] hover:bg-[#3AB0E1] text-white font-semibold text-sm px-5 py-2.5 rounded-full transition-colors duration-300"
          >
            Schedule a Demo
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
          </button>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-[#263562]"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-b border-[#263562]/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.testid}
                  data-testid={`${l.testid}-mobile`}
                  onClick={() => go(l.target)}
                  className="text-left font-display text-xl font-bold text-[#263562] hover:text-[#3AB0E1] transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="cta-schedule-demo-mobile"
                onClick={() => go("#contact")}
                className="mt-2 bg-[#263562] text-white font-semibold px-5 py-3 rounded-full w-fit"
              >
                Schedule a Demo
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
